// Triggers: list, plus create/delete for callers with `manage_triggers` (rbac-restore R2,
// task 2.1). Create is plan-limited by `triggers.max` (POST /triggers runs
// require_capacity), so "New trigger" is disabled with the remedy once usage reaches it.
import { useId, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { errorMessage } from '../api/errors'
import { remedyFor, useCapacity, useEntitlements, useInvalidateEntitlements } from '../entitlements'
import { UpsellLink } from '../entitlements/PlanNote'
import { qk } from '../api/queries'
import type { ConnectedAccountsResponse, TriggerCreateRequest, TriggerResponse } from '../api/types'
import { useSession } from '../context/SessionContext'
import { useCan } from '../rbac'
import { CopyButton } from '../components/CopyButton'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { formatDate } from '../components/format'
import { Badge, EmptyState, PageHeader, QueryState } from '../components/ui'

const REASON: Record<string, string> = {
  needs_reconnect: 'Needs reconnect',
  delivery_failures: 'Delivery failures',
}

type Mechanism = TriggerCreateRequest['mechanism']

function NewTriggerModal({ onClose }: { onClose: () => void }) {
  const id = useId()
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const invalidateEntitlements = useInvalidateEntitlements()
  const accounts = useQuery({
    queryKey: qk.connectedAccounts(workspaceId),
    queryFn: () => apiFetch<ConnectedAccountsResponse>('/connected-accounts'),
  })
  const list = accounts.data?.accounts ?? []

  const [accountId, setAccountId] = useState('')
  const [mechanism, setMechanism] = useState<Mechanism>('webhook')
  const [secret, setSecret] = useState('')
  const [calendarId, setCalendarId] = useState('')
  const [configText, setConfigText] = useState('{}')
  const [touched, setTouched] = useState(false)

  const account = list.find((a) => a.id === accountId)
  const provider = account?.provider ?? ''
  const needsCalendar = provider === 'google' && mechanism === 'poll'

  let config: Record<string, unknown> | null = null
  try {
    const parsed: unknown = JSON.parse(configText.trim() || '{}')
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) config = parsed as Record<string, unknown>
  } catch {
    config = null
  }

  const problems: string[] = []
  if (!account) problems.push('Choose a connected account.')
  if (mechanism === 'webhook' && !secret.trim()) problems.push('A signing secret is required for webhook triggers.')
  if (config === null) problems.push('Config must be a JSON object.')
  if (needsCalendar && !calendarId.trim() && !(config && typeof config.calendar_id === 'string' && config.calendar_id)) {
    problems.push('Google polling triggers need a calendar ID.')
  }

  const create = useMutation({
    mutationFn: (body: TriggerCreateRequest) =>
      apiFetch<TriggerResponse>('/triggers', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.triggers(workspaceId) })
      invalidateEntitlements()
    },
  })

  const created = create.data
  if (created) {
    return (
      <Modal
        title="Trigger created"
        onClose={onClose}
        footer={<button type="button" className="btn btn--primary" onClick={onClose}>Done</button>}
      >
        <p>
          <strong>{created.provider}</strong> ({created.mechanism}) is subscribed.
        </p>
        {created.ingress_url && (
          <>
            <p className="warn-note">
              Point the provider’s webhook at this ingress URL. Copy it now: it is only listed again on the trigger’s detail
              route, never in this table.
            </p>
            <div className="secret-row">
              <code className="secret" aria-label="Ingress URL">{created.ingress_url}</code>
              <CopyButton value={created.ingress_url} />
            </div>
          </>
        )}
      </Modal>
    )
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (problems.length > 0 || create.isPending || !account || config === null) return
    const finalConfig = needsCalendar && calendarId.trim() ? { ...config, calendar_id: calendarId.trim() } : config
    const body: TriggerCreateRequest = {
      provider: account.provider,
      mechanism,
      connected_account_id: account.id,
      config: finalConfig,
      ...(mechanism === 'webhook' ? { signing_secret: secret } : {}),
    }
    create.mutate(body)
  }

  return (
    <Modal title="New trigger" onClose={onClose}>
      <form onSubmit={submit} aria-label="New trigger form" noValidate>
        <div className="field">
          <label htmlFor={`${id}-acct`}>Connected account</label>
          <select id={`${id}-acct`} value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={accounts.isLoading}>
            <option value="">{accounts.isLoading ? 'Loading…' : 'Choose an account'}</option>
            {list.map((a) => (
              <option key={a.id} value={a.id}>
                {a.provider}{a.provider_account_id ? ` · ${a.provider_account_id}` : ''}{a.status === 'needs_reconnect' ? ' (needs reconnect)' : ''}
              </option>
            ))}
          </select>
          {!accounts.isLoading && list.length === 0 && (
            <span className="field-hint">You have no connected accounts. Connect an app in Toolkits first.</span>
          )}
          {accounts.error ? <span className="field-hint">{errorMessage(accounts.error)}</span> : null}
        </div>

        <fieldset className="radio-row">
          <legend className="field-hint">Mechanism</legend>
          <label className="check">
            <input type="radio" name={`${id}-mech`} checked={mechanism === 'webhook'} onChange={() => setMechanism('webhook')} />
            Webhook <span className="cell-sub">(the provider pushes events)</span>
          </label>
          <label className="check">
            <input type="radio" name={`${id}-mech`} checked={mechanism === 'poll'} onChange={() => setMechanism('poll')} />
            Poll <span className="cell-sub">(Symone checks for changes)</span>
          </label>
        </fieldset>

        {mechanism === 'webhook' && (
          <div className="field">
            <label htmlFor={`${id}-secret`}>Signing secret</label>
            <input
              id={`${id}-secret`}
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
            <span className="field-hint">The provider’s webhook signing secret. Stored encrypted and never shown again.</span>
          </div>
        )}

        {needsCalendar && (
          <div className="field">
            <label htmlFor={`${id}-cal`}>Calendar ID</label>
            <input id={`${id}-cal`} value={calendarId} onChange={(e) => setCalendarId(e.target.value)} placeholder="primary" />
          </div>
        )}

        <div className="field">
          <label htmlFor={`${id}-config`}>Config (JSON)</label>
          <textarea
            id={`${id}-config`}
            className="code-input"
            rows={4}
            spellCheck={false}
            value={configText}
            onChange={(e) => setConfigText(e.target.value)}
          />
          <span className="field-hint">Mechanism-specific, e.g. {'{"event_types": ["message"]}'} for a Slack webhook.</span>
        </div>

        {touched && problems.length > 0 && (
          <div className="form-error" role="alert">
            {problems.map((p) => <p key={p}>{p}</p>)}
          </div>
        )}
        {create.error ? <p className="form-error" role="alert">{errorMessage(create.error)}</p> : null}
        <div className="modal-foot modal-foot--inline">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={create.isPending} aria-busy={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create trigger'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function DeleteTriggerModal({ trigger, onClose }: { trigger: TriggerResponse; onClose: () => void }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const invalidateEntitlements = useInvalidateEntitlements()
  const del = useMutation({
    mutationFn: () => apiFetch(`/triggers/${encodeURIComponent(trigger.id)}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.triggers(workspaceId) })
      invalidateEntitlements()
      onClose()
    },
  })
  return (
    <Modal
      title="Delete trigger"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={del.isPending}
            aria-busy={del.isPending}
            onClick={() => del.mutate()}
          >
            {del.isPending ? 'Deleting…' : 'Delete trigger'}
          </button>
        </>
      }
    >
      <p>
        Delete the <strong>{trigger.provider}</strong> {trigger.mechanism} trigger? Symone stops delivering its events
        immediately, and a webhook trigger’s ingress URL stops accepting requests. This can’t be undone.
      </p>
      {del.error ? <p className="form-error" role="alert">{errorMessage(del.error)}</p> : null}
    </Modal>
  )
}

export function TriggersView() {
  const { workspaceId } = useSession()
  const canManage = useCan('manage_triggers')
  const capacity = useCapacity('triggers.max', 'triggers')
  const ent = useEntitlements().data
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<TriggerResponse | null>(null)
  const q = useQuery({
    queryKey: qk.triggers(workspaceId),
    queryFn: () => apiFetch<TriggerResponse[]>('/triggers'),
  })
  const triggers = q.data ?? []
  // Disabled while the plan is loading or unreadable (fail closed), and at the limit.
  const blocked = canManage && !capacity.allowed
  const showNote = blocked && !capacity.loading
  const atLimit = capacity.used !== undefined ? capacity.limit : undefined

  const newButton = canManage && (
    <button
      type="button"
      className="btn btn--primary"
      disabled={blocked}
      aria-describedby={showNote ? 'triggers-limit' : undefined}
      onClick={() => setCreating(true)}
    >
      <Icon name="plus" size={14} /> New trigger
    </button>
  )

  return (
    <div className="page">
      <PageHeader
        title="Triggers"
        count={q.data ? triggers.length : undefined}
        description="Subscriptions that push provider events (webhooks or polling) to your agents."
        actions={newButton}
      />
      {showNote && (
        <p className="warn-note" id="triggers-limit" role="note">
          {typeof atLimit === 'number' ? (
            <>
              {`Your plan allows up to ${atLimit} trigger${atLimit === 1 ? '' : 's'} in this workspace. ${remedyFor(ent, 'triggers.max')}`}{' '}
              <UpsellLink />
            </>
          ) : (
            capacity.reason
          )}
        </p>
      )}
      <QueryState isLoading={q.isLoading} error={q.error} what="triggers" onRetry={() => q.refetch()}>
        {triggers.length === 0 ? (
          <EmptyState
            icon="bolt"
            title="No triggers yet"
            body={
              canManage
                ? 'Create a trigger to have Symone deliver events (new issues, calendar changes, messages) to your agent.'
                : 'Triggers have Symone deliver events (new issues, calendar changes, messages) to your agent. Ask a workspace owner or admin to create one.'
            }
            sampleHeaders={['Provider', 'Mechanism', 'Status', 'Created']}
            sampleRows={[
              ['issues', 'webhook', <Badge tone="success">active</Badge>, 'Oct 2, 2026'],
              ['calendar', 'poll', <Badge>disabled</Badge>, 'Sep 21, 2026'],
            ]}
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Mechanism</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>ID</th>
                  {canManage && <th><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {triggers.map((t) => (
                  <tr key={t.id}>
                    <td className="cell-strong">{t.provider}</td>
                    <td>{t.mechanism}</td>
                    <td>
                      <Badge tone={t.status === 'active' ? 'success' : 'neutral'}>{t.status}</Badge>
                      {t.disabled_reason && <div className="cell-sub">{REASON[t.disabled_reason] ?? t.disabled_reason}</div>}
                    </td>
                    <td>{formatDate(t.created_at)}</td>
                    <td><code className="muted">{t.id.slice(0, 8)}</code></td>
                    {canManage && (
                      <td className="cell-actions">
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost-danger"
                          aria-label={`Delete ${t.provider} ${t.mechanism} trigger`}
                          onClick={() => setDeleting(t)}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>

      {creating && <NewTriggerModal onClose={() => setCreating(false)} />}
      {deleting && <DeleteTriggerModal trigger={deleting} onClose={() => setDeleting(null)} />}
    </div>
  )
}
